import React from 'react';
import { Outlet, Route, Routes, Navigate } from 'react-router-dom';

import AuthProvider from './AuthProvider';
import Layout from './Layout';
import ProtectedRoute from './ProtectedRoute';
import AdminRoute from './AdminRoute';

import Home from './components/pages/Home';
import NotFound from './components/pages/NotFound';
import RunReports from './components/pages/RunReports';
import CrimeExplore from './components/pages/CrimeExplore';
import ExpansionExplorer from './components/pages/ExpansionExplorer';
import Zipfinder from './components/pages/Zipfinder';
import AdminPage from './components/pages/AdminPanel/AdminPage';
import BulkImport from './components/pages/BulkVestmaps';
import PlaceHolderPage from './components/pages/PlaceHolderPage';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import ReportTemplates from './components/pages/ReportTemplates';
import TestGround from './components/pages/TestGround';
import TestWebmap from './components/pages/TestWebmap';
import PublicReport from './components/pages/PublicReport';
import UserView from './components/pages/AdminPanel/UserView';
import Agent from './components/pages/Agent';
import AgentV2 from './components/pages/AgentV2';
import Discover from './components/pages/Discover';
import SharedGroup from './components/pages/SharedGroup';
import SharedCombinedReport from './components/pages/SharedCombinedReport';
import SharedUnitView from './components/pages/SharedUnitView';
import SharedDiscover from './components/pages/SharedDiscover';
import SharedMap from './components/pages/SharedMap';
import ComingSoon from './components/pages/ComingSoon';
import useMobileRedirect from './hooks/useMobileRedirect';
import RentAdvisor from './components/pages/RentAdvisor';
import BoundaryChecker from './components/pages/BoundaryChecker';
import McpSetup from './components/pages/McpSetup';
import { AGENT_ENABLED, DEFAULT_LANDING_PATH } from './featureFlags';

import './App.css';

const theme = createTheme({
  typography: {
    fontFamily: `'Lato', 'sans-serif'`,
  },
});

const App = () => {
  // useMobileRedirect();

  return (
    <ThemeProvider theme={theme}>
      <div className='App'>
        <AuthProvider>
          <Layout>
            <Routes>
              <Route
                path='/'
                element={<Navigate to={DEFAULT_LANDING_PATH} replace />}
              />

              <Route
                path='/expansion-explorer'
                element={
                  <ProtectedRoute>
                    <ExpansionExplorer />
                  </ProtectedRoute>
                }
              />

              <Route
                path='/crime-explorer'
                element={
                  <ProtectedRoute>
                    <CrimeExplore />
                  </ProtectedRoute>
                }
              />

              <Route
                path='/zipfinder'
                element={
                  <ProtectedRoute>
                    <Zipfinder />
                  </ProtectedRoute>
                }
              />

              <Route
                path='/run-reports'
                element={
                  <ProtectedRoute>
                    <RunReports />
                  </ProtectedRoute>
                }
              />

              <Route
                path='/bulk-import'
                element={
                  <ProtectedRoute>
                    <BulkImport />
                  </ProtectedRoute>
                }
              />

              <Route
                path='/admin-panel'
                element={
                  <AdminRoute>
                    <AdminPage />
                  </AdminRoute>
                }
              />

              <Route
                path='/in-progress'
                element={
                  <ProtectedRoute>
                    <PlaceHolderPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path='/templates'
                element={
                  <AdminRoute>
                    <ReportTemplates />
                  </AdminRoute>
                }
              />

              <Route
                path='/rent-advisor'
                element={
                  <ProtectedRoute>
                    <RentAdvisor />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/boundary-checker'
                element={
                  <ProtectedRoute>
                    <BoundaryChecker />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/agent'
                element={
                  AGENT_ENABLED ? (
                    <ProtectedRoute>
                      <Agent />
                    </ProtectedRoute>
                  ) : (
                    <Navigate to={DEFAULT_LANDING_PATH} replace />
                  )
                }
              />

              <Route
                path='/agent_v2'
                element={
                  AGENT_ENABLED ? (
                    <AdminRoute>
                      <AgentV2 />
                    </AdminRoute>
                  ) : (
                    <Navigate to={DEFAULT_LANDING_PATH} replace />
                  )
                }
              />

              <Route
                path='/discover'
                element={
                  <ProtectedRoute>
                    <Discover />
                  </ProtectedRoute>
                }
              />

              <Route path='/mcp' element={<McpSetup />} />
              <Route path='/report/:reportID' element={<PublicReport />} />
              <Route path='/shared-unit/:rentGridId/:unitId' element={<SharedUnitView />} />
              <Route path='/shared-group' element={<SharedGroup />} />
              <Route
                path='/shared-combined-report'
                element={<SharedCombinedReport />}
              />
              <Route path='/shared-discover/:shareId' element={<SharedDiscover />} />
              <Route path='/maps/:shareId' element={<SharedMap />} />
              <Route
                path='/user-view/:userID'
                element={
                  <ProtectedRoute>
                    <UserView />
                  </ProtectedRoute>
                }
              />

              <Route
                path='/coming-soon'
                element={
                  <ProtectedRoute>
                    <ComingSoon />
                  </ProtectedRoute>
                }
              />

              <Route path='*' element={<NotFound />} />

              {/* testing routes for new features and stuff */}
              <Route
                path='/test-ground'
                element={
                  <ProtectedRoute>
                    <TestGround />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/test-webmap'
                element={
                  <ProtectedRoute>
                    <TestWebmap />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Layout>
        </AuthProvider>
      </div>
    </ThemeProvider>
  );
};

export default App;
